import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { userService } from "@/services/userService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface UserRef {
  id: string;
  first_name?: string;
  last_name?: string;
  preferred_name?: string;
  email_address?: string;
}

interface UsersInputProps {
  /** Array of user objects */
  value: UserRef[];
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (users: UserRef[]) => void;
}

/** Helper to get display name: preferred_name OR first_name + last_name */
function getDisplayName(user: UserRef): string {
  if (user.preferred_name) return user.preferred_name;
  return [user.first_name, user.last_name].filter(Boolean).join(" ") || "Unknown";
}

export function UsersInput({ value, state = "editing", label = "Users", error, onChange }: UsersInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  const handleChipPress = useCallback(
    (userId: string) => {
      const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
      const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
      if (currentPanel && currentId) {
        pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
      }
      navigateToEntity("user", userId);
    },
    [currentParams, pushNavigation, currentEntity, navigateToEntity],
  );

  // Fetch all user options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const users = await userService.getUsers();
        if (cancelled) return;
        setOptions(
          users.map((u) => ({
            id: u.id!,
            name: getDisplayName({
              id: u.id!,
              first_name: u.first_name ?? undefined,
              last_name: u.last_name ?? undefined,
              preferred_name: u.preferred_name ?? undefined,
              email_address: u.email_address ?? undefined,
            }),
            description: u.email_address ?? undefined,
            avatar: {
              userId: u.id ?? undefined,
              firstName: u.first_name ?? undefined,
              lastName: u.last_name ?? undefined,
              email: u.email_address ?? undefined,
            },
          })),
        );
      } catch (err) {
        console.error("Failed to load users:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items from value
  const chipItems = useMemo<ChipItem[]>(() => {
    return value.map((user) => ({
      id: user.id,
      name: getDisplayName(user),
      readonly: false,
      avatar: {
        userId: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email_address,
      },
    }));
  }, [value]);

  const handleAdd = useCallback(
    (userId: string) => {
      if (value.some((u) => u.id === userId)) return;
      const opt = options.find((o) => o.id === userId);
      if (!opt || !opt.avatar) return;
      onChange?.([
        ...value,
        {
          id: opt.id,
          first_name: opt.avatar.firstName,
          last_name: opt.avatar.lastName,
          preferred_name: undefined,
          email_address: opt.avatar.email,
        },
      ]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (userId: string) => {
      onChange?.(value.filter((u) => u.id !== userId));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="account"
      accentTone="violet"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search users..."
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onChipPress={handleChipPress}
    />
  );
}
